import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@finster/ui";
import { ScrollText } from "lucide-react";
import { useAnchoredOverlay } from "../hooks/useAnchoredOverlay";
import { getSkillSlashOptionId } from "../selection";
import { MAX_SELECTED_SKILLS, type SkillSummary } from "../types";

export interface SkillSlashSuggestionsProps {
  open: boolean;
  skills: readonly SkillSummary[];
  activeIndex: number;
  listboxId: string;
  atLimit?: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onActiveIndexChange: (index: number) => void;
  onSelect: (skill: SkillSummary) => void;
  onDismiss: () => void;
  className?: string;
}

const SKILL_LIMIT_NOTICE = `You can add up to ${MAX_SELECTED_SKILLS} Skills. Remove one to add another.`;

export const SkillSlashSuggestions: React.FC<SkillSlashSuggestionsProps> = ({
  open,
  skills,
  activeIndex,
  listboxId,
  atLimit = false,
  anchorRef,
  onActiveIndexChange,
  onSelect,
  onDismiss,
  className,
}) => {
  const fallbackAnchorRef = React.useRef<HTMLSpanElement>(null);
  const effectiveAnchorRef = anchorRef ?? fallbackAnchorRef;
  const panelRef = React.useRef<HTMLDivElement>(null);
  const position = useAnchoredOverlay({
    open,
    anchorRef: effectiveAnchorRef,
    width: 244,
    // 171px of design content (1px rule + 26px header + four 36px rows) plus the 1px panel border.
    maxHeight: 173,
    preferredSide: "top",
  });

  React.useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !effectiveAnchorRef.current?.contains(target)) {
        onDismiss();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [effectiveAnchorRef, onDismiss, open]);

  React.useEffect(() => {
    if (!open || atLimit || activeIndex < 0) return;
    const activeSkill = skills[activeIndex];
    if (!activeSkill) return;
    document
      .getElementById(getSkillSlashOptionId(listboxId, activeSkill.id))
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, atLimit, listboxId, open, skills]);

  return (
    <>
      {!anchorRef && (
        <span
          ref={fallbackAnchorRef}
          className="pointer-events-none absolute inset-x-0 top-0"
          aria-hidden
        />
      )}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-skill-slash-suggestions=""
            className={cn(
              "fixed z-[2147483647] flex flex-col overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-xs",
              className
            )}
            style={{
              left: position?.left,
              top: position?.top,
              bottom: position?.bottom,
              width: position?.width,
              maxHeight: position?.maxHeight,
              visibility: position ? "visible" : "hidden",
            }}
          >
            <div className="h-px shrink-0 bg-border" aria-hidden />
            <div
              id={`${listboxId}-label`}
              className="flex h-[26px] min-h-[26px] items-center px-4 text-xs font-medium text-muted-foreground"
            >
              All Skills
            </div>
            {atLimit ? (
              <div
                role="status"
                aria-live="polite"
                className="flex min-h-9 items-center px-4 py-2 text-sm text-muted-foreground"
              >
                {SKILL_LIMIT_NOTICE}
              </div>
            ) : (
              <div
                id={listboxId}
                role="listbox"
                aria-labelledby={`${listboxId}-label`}
                className="min-h-0 max-h-36 flex-1 overflow-y-auto"
              >
                {skills.map((skill, index) => {
                  const active = index === activeIndex;
                  return (
                    <button
                      key={skill.id}
                      id={getSkillSlashOptionId(listboxId, skill.id)}
                      type="button"
                      role="option"
                      aria-selected={active}
                      tabIndex={-1}
                      title={skill.name}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseMove={() => onActiveIndexChange(index)}
                      onClick={() => onSelect(skill)}
                      className={cn(
                        "flex h-9 w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-4 text-left text-sm font-medium",
                        "hover:bg-accent focus:outline-none",
                        active && "bg-accent"
                      )}
                    >
                      <ScrollText className="size-[18px] shrink-0 text-[#525252]" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">/{skill.slug}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};
